const { v4 } = require('uuid')
const { generatePaymentPlanData, addAmountPaidToInstalments, getPlanDurationInWeeks } = require('./tools')
const { installmentsQuery } = require('./queries/payment-installments-queries')
const { LOCATION_CLASSIFICATIONS } = require('../location/tools')
const { mapInserts } = require('../tools/sql-tools')
const smartId = require('../tools/smart-id')
const { syncPaymentPlanBalances } = require('../finances/sync')

const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000

const httpError = (message, status) => {
  const error = new Error(message)
  error.status = status
  return error
}

const valueOr = (value, fallback) => value === undefined ? fallback : value

const toCalendarDate = value => value instanceof Date
  ? [value.getFullYear(), value.getMonth() + 1, value.getDate()].map(part => String(part).padStart(2, '0')).join('-')
  : value

class PgAdapter {
  constructor (pgConnection) {
    this.pgConnection = pgConnection
  }

  async withTransaction (fn) {
    const client = await this.pgConnection.connect()
    try {
      await client.query('BEGIN')
      const result = await fn(client)
      await client.query('COMMIT')
      return result
    } catch (error) {
      await client.query('ROLLBACK')
      throw error
    } finally {
      client.release()
    }
  }

  async getLocation (locationId) {
    const { rows: [location] } = await this.pgConnection.query(`
      select
        l.fsid,
        upper(m.country_code) as "countryCode",
        uuid as "locationId",
        classification,
        membership_type as "membershipType",
        (
          has_60_day_payment_option is true OR
          has_90_day_payment_option is true
        )::boolean AS "has60And90DayOption",
        l.pay_per_use as "isPayPerUse",
        l.fee_percentage as "feePercentage",
        l.fee_percentage_long_term as "feePercentageLongTerm"
      from avocado.data_location l
      left join avocado.data_market m on l.market_id = m.id
      where uuid = $1
    `, [locationId])

    if (!location) {
      throw httpError('Location not found', 404)
    }
    return location
  }

  async getInvoiceByDocNumber (docNumber, countryCode) {
    const { rows: [invoice] } = await this.pgConnection.query(`
      select txn_id::text as "txnId", doc_no as "docNumber"
      from fin.invoice
      where doc_no = $1 and company::text = $2
      limit 1
    `, [docNumber, countryCode])
    return invoice
  }

  async getAccountIdPaymentPlan (accountId, countryCode, excludePaymentPlanId = null) {
    const { rows: [existingRow] } = await this.pgConnection.query(`
      select * from avocado.data_paymentplan
      where quickbooks_account_id = $1
        and lower(company_code) = lower($2)
        and ($3::uuid is null or id != $3::uuid)
      limit 1
    `, [accountId, countryCode, excludePaymentPlanId])
    return existingRow
  }

  async getPaymentPlan (id) {
    const { rows: [paymentPlan] } = await this.pgConnection.query(`
      select
        pp.*,
        l.name_of_pharmacy as location_name,
        l.mobilized_date as location_mobilized_date,
        l.classification as location_classification,
        (
          l.has_60_day_payment_option is true OR
          l.has_90_day_payment_option is true
        )::boolean as location_has_60_and_90_day_option,
        invoice.qbo_txn_id as invoice_txn_id,
        invoice.doc_no as invoice_doc_number,
        coalesce((
          select jsonb_agg(ppi order by ppi.due_date)
          from avocado.data_paymentplaninstallment ppi
          where ppi.payment_plan_id = pp.id
        ), '[]'::jsonb) as instalments
      from avocado.data_paymentplan pp
      left join avocado.data_location l on l.uuid = pp.location_id
      left join lateral (
        select ipp.qbo_txn_id, i.doc_no
        from avocado.data_invoicepaymentplan ipp
        left join fin.invoice i
          on i.txn_id::text = ipp.qbo_txn_id and i.company::text = ipp.qbo_company_code
        where ipp.paymentplan_id = pp.id and coalesce(ipp._deleted, false) is false
        limit 1
      ) invoice on true
      where pp.id = $1
    `, [id])

    if (!paymentPlan) {
      throw httpError('This payment plan could not be found. It may have been deleted; refresh the list and try again.', 404)
    }
    return paymentPlan
  }

  async getLocationNames (locationIds) {
    const { rows } = await this.pgConnection.query(`
      select uuid as "locationId", name_of_pharmacy as "name"
      from avocado.data_location
      where uuid = any($1::text[])
    `, [locationIds])
    return rows.reduce((names, { locationId, name }) => ({ ...names, [locationId]: name }), {})
  }

  async updatePaymentPlan (client, id, changes) {
    const columns = Object.keys(changes)
    const setClause = columns.map((column, index) => `${column} = $${index + 2}`).join(', ')
    const { rows: [paymentPlan] } = await client.query(`
      update avocado.data_paymentplan
      set ${setClause}, updated_at = now()
      where id = $1
      returning *
    `, [id, ...columns.map(column => changes[column])])
    return paymentPlan
  }

  // instalment rows are a stored copy of what installmentsQuery derives from the plan row
  async replaceInstalments (client, paymentPlanId) {
    await client.query('delete from avocado.data_paymentplaninstallment where payment_plan_id = $1', [paymentPlanId])
    const { rows } = await client.query(installmentsQuery(), [paymentPlanId])
    if (!rows.length) {
      return []
    }
    const { fields, flattened, dollarSigns } = mapInserts(rows.map(instalment => ({
      id: v4(),
      amount: instalment.amount,
      is_paid: instalment.is_paid,
      balance: instalment.balance,
      payment_plan_id: paymentPlanId,
      code: instalment.code,
      due_date: instalment.due_date
    })))
    const { rows: instalments } = await client.query(`
      insert into avocado.data_paymentplaninstallment (${fields.join(', ')})
      values ${dollarSigns}
      returning *
    `, flattened)
    return instalments
  }

  // the delete trigger on data_paymentplan soft-deletes the plan's data_invoicepaymentplan link
  async deletePaymentPlan (id) {
    return this.withTransaction(async client => {
      await client.query('delete from avocado.data_paymentplaninstallment where payment_plan_id = $1', [id])
      await client.query('delete from avocado.data_paymentplan where id = $1', [id])
    })
  }

  async setLocation60And90DayOption (locationId, enabled) {
    await this.pgConnection.query(`
      update avocado.data_location
      set has_60_day_payment_option = $2, has_90_day_payment_option = $2
      where uuid = $1
    `, [locationId, enabled])
  }
}

class PaymentPlanManagement {
  /**
   * @param {import('./payment-plans-adapter.js')} paymentPlanAdapter
   * @param {import('./payment-installment-adapter.js')} installmentAdapter
   * @param {import('./payment-plan-options-adapter.js')} paymentPlanOptionsAdapter
   */
  constructor (pgConnection, paymentPlanAdapter, installmentAdapter, paymentPlanOptionsAdapter) {
    this.pgConnection = pgConnection
    this.pgAdapter = new PgAdapter(pgConnection)
    this.paymentPlanAdapter = paymentPlanAdapter
    this.installmentAdapter = installmentAdapter
    this.paymentPlanOptionsAdapter = paymentPlanOptionsAdapter
  }

  // set after construction: the quickbooks api is built from this api
  setQuickbooksApi (quickbooksApi) {
    this.quickbooksApi = quickbooksApi
  }

  // as auto reconciliation does: the plan balance and paid status follow the loan account
  async syncLoanAccountBalance (loanAccountId, companyCode) {
    if (!this.quickbooksApi || !loanAccountId) {
      return false
    }
    const [account] = await this.quickbooksApi.entities.account.getList([loanAccountId], companyCode)
    if (!account) {
      return false
    }
    await syncPaymentPlanBalances(this.pgConnection, [{
      origin: 'qbo',
      company: companyCode.toUpperCase(),
      accountId: account.Id,
      balance: account.CurrentBalance
    }])
    return true
  }

  async syncBalance (id) {
    const paymentPlan = await this.pgAdapter.getPaymentPlan(id)
    if (!paymentPlan.quickbooks_account_id) {
      throw httpError('Add the loan account id to this payment plan before syncing its balance.', 400)
    }
    const synced = await this.syncLoanAccountBalance(paymentPlan.quickbooks_account_id, paymentPlan.company_code)
    if (!synced) {
      throw httpError(`Loan account ${paymentPlan.quickbooks_account_id} could not be found in QuickBooks. Check the loan account id on this payment plan and try again.`, 404)
    }
    return this.getOne(id)
  }

  async assertLoanAccountIsFree (loanAccountId, countryCode, paymentPlanId) {
    const existingAccountPaymentPlan = await this.pgAdapter.getAccountIdPaymentPlan(loanAccountId, countryCode, paymentPlanId)
    if (existingAccountPaymentPlan) {
      throw httpError(`Loan account ${loanAccountId} already has existing payment plan`, 409)
    }
  }

  generateOptions (location, pastDueAmount) {
    return this.paymentPlanOptionsAdapter.generatePaymentPlanOptions({
      countryCode: location.countryCode,
      classification: location.classification,
      orderAmount: pastDueAmount,
      membershipType: location.membershipType,
      has60And90DayOption: location.has60And90DayOption
    })
  }

  buildPaymentPlan (location, { startDate, pastDueAmount, term, frequency, separateServiceFeeAndPrincipal }) {
    const paymentPlanOptions = this.generateOptions(location, pastDueAmount)

    const selectedOption = paymentPlanOptions[frequency] && paymentPlanOptions[frequency][term]
    if (!selectedOption || selectedOption.frequency !== frequency) {
      throw httpError(`A ${term} x ${frequency} payment plan is not available to this location for this amount. Choose one of the payment options the location qualifies for.`, 400)
    }

    const { state } = smartId.parse(location.fsid)
    return generatePaymentPlanData({
      invoiceData: { balance: pastDueAmount, location_id: location.locationId, company_code: location.countryCode },
      market: state,
      isPayPerUseClient: location.isPayPerUse,
      feePercentage: location.feePercentage,
      feePercentageLongTerm: location.feePercentageLongTerm,
      startDate,
      serviceFeePercentage: selectedOption.serviceFeePercentage,
      term,
      frequency,
      separateServiceFeeAndPrincipal
    })
  }

  async create ({
    locationId,
    startDate,
    pastDueAmount,
    term,
    frequency,
    loanAccountId,
    isActive,
    invoiceDocNumber,
    separateServiceFeeAndPrincipal
  }) {
    const location = await this.pgAdapter.getLocation(locationId)
    await this.assertLoanAccountIsFree(loanAccountId, location.countryCode)

    let invoice = null
    if (invoiceDocNumber) {
      invoice = await this.pgAdapter.getInvoiceByDocNumber(invoiceDocNumber, location.countryCode)
      if (!invoice) {
        throw httpError(`Invoice ${invoiceDocNumber} was not found for this location's country. Check the invoice number and try again.`, 404)
      }
    }

    const { paymentPlan: paymentPlanData, serviceFeeAmount, percentageFee } = this.buildPaymentPlan(location, {
      startDate,
      pastDueAmount,
      term,
      frequency,
      separateServiceFeeAndPrincipal
    })
    paymentPlanData.quickbooks_account_id = loanAccountId

    const paymentPlan = await this.paymentPlanAdapter.createPaymentPlan(
      paymentPlanData,
      invoice && invoice.txnId,
      serviceFeeAmount,
      percentageFee,
      invoiceDocNumber
    )

    if (isActive) {
      await this.activate(paymentPlan.id)
    }
    await this.syncLoanAccountBalance(loanAccountId, location.countryCode)
    return this.getOne(paymentPlan.id)
  }

  async update (id, changes) {
    const current = await this.pgAdapter.getPaymentPlan(id)
    if (current.is_paid) {
      throw httpError('This payment plan has been fully paid and can no longer be changed.', 400)
    }
    if (
      changes.separateServiceFeeAndPrincipal !== undefined &&
      changes.separateServiceFeeAndPrincipal !== current.separate_service_fee_and_principal
    ) {
      throw httpError('How the service fee is billed cannot be changed on an existing payment plan. Delete this payment plan and create a new one instead.', 400)
    }
    if (changes.loanAccountId !== undefined && changes.loanAccountId !== current.quickbooks_account_id) {
      throw httpError('The loan account of a payment plan cannot be changed. Delete this payment plan and create a new one against the right loan account instead.', 400)
    }
    if (changes.pastDueAmount !== undefined && changes.pastDueAmount !== Number(current.past_due)) {
      throw httpError('The past due amount of a payment plan cannot be changed. Delete this payment plan and create a new one for the right amount instead.', 400)
    }
    if (changes.isActive === false && current.is_active) {
      throw httpError('An active payment plan cannot be made inactive. Delete it instead if it no longer applies.', 400)
    }

    const location = await this.pgAdapter.getLocation(current.location_id)
    const row = {}

    const termsChanged = ['startDate', 'term', 'frequency'].some(key => changes[key] !== undefined)
    if (termsChanged) {
      const { paymentPlan: rebuilt } = this.buildPaymentPlan(location, {
        startDate: valueOr(changes.startDate, current.start_date),
        pastDueAmount: current.past_due,
        term: valueOr(changes.term, current.term),
        frequency: valueOr(changes.frequency, current.frequency),
        separateServiceFeeAndPrincipal: current.separate_service_fee_and_principal
      })
      // a separately billed service fee was booked as a fee event when the plan was created
      if (current.separate_service_fee_and_principal && rebuilt.service_fee !== current.service_fee) {
        throw httpError('This change would alter a service fee that has already been billed separately. Delete this payment plan and create a new one instead.', 400)
      }
      Object.assign(row, {
        start_date: rebuilt.start_date,
        past_due: rebuilt.past_due,
        installment_amount: rebuilt.installment_amount,
        term: rebuilt.term,
        service_fee: rebuilt.service_fee,
        waive_service_fee: rebuilt.waive_service_fee,
        frequency: rebuilt.frequency
      })
      // once active, the balance is synced from the QuickBooks loan account instead
      if (!current.is_active) {
        row.quickbooks_account_balance = rebuilt.past_due
      }
    }

    const activating = changes.isActive === true && !current.is_active

    if (Object.keys(row).length) {
      await this.pgAdapter.withTransaction(async client => {
        await this.pgAdapter.updatePaymentPlan(client, id, row)
        if (termsChanged && current.instalments.length) {
          await this.pgAdapter.replaceInstalments(client, id)
        }
      })
    }

    if (activating) {
      await this.activate(id)
    }
    await this.syncLoanAccountBalance(current.quickbooks_account_id, current.company_code)
    return this.getOne(id)
  }

  async delete (id) {
    await this.pgAdapter.getPaymentPlan(id)
    await this.pgAdapter.deletePaymentPlan(id)
  }

  async activate (id) {
    const paymentPlan = await this.pgAdapter.getPaymentPlan(id)
    if (!paymentPlan.quickbooks_account_id) {
      throw httpError('Add the loan account id to this payment plan before activating it.', 400)
    }
    if (!paymentPlan.is_active || !paymentPlan.instalments.length) {
      await this.pgAdapter.withTransaction(async client => {
        if (!paymentPlan.instalments.length) {
          await this.pgAdapter.replaceInstalments(client, id)
        }
        await this.pgAdapter.updatePaymentPlan(client, id, { is_active: true })
      })
    }
    return this.getOne(id)
  }

  async list (limit, offset, { locationId, loanAccountId, isActive } = {}) {
    const filter = {}
    if (locationId) filter.location_id = locationId
    if (loanAccountId) filter.quickbooks_account_id = loanAccountId
    if (isActive !== undefined) filter.is_active = isActive

    const page = await this.paymentPlanAdapter.getList({
      limit,
      offset,
      filter,
      ordering: ['-created_at', 'id']
    })
    const locationNames = await this.pgAdapter.getLocationNames([...new Set(page.results.map(plan => plan.location_id))])
    return {
      ...page,
      results: page.results.map(plan => ({
        ...plan,
        start_date: toCalendarDate(plan.start_date),
        location_name: locationNames[plan.location_id] || null
      }))
    }
  }

  async getPaymentOptions (locationId, pastDueAmount) {
    const location = await this.pgAdapter.getLocation(locationId)
    const generated = this.generateOptions(location, pastDueAmount)
    const options = Object.keys(generated).reduce((all, frequency) => all.concat(
      Object.values(generated[frequency] || {})
        .filter(option => option && option.frequency === frequency)
        .map(({ term, serviceFeePercentage }) => ({ frequency, term, serviceFeePercentage: serviceFeePercentage || 0 }))
    ), [])
    return {
      locationId,
      classification: location.classification,
      has60And90DayOption: location.has60And90DayOption,
      options
    }
  }

  async getOne (id) {
    const paymentPlan = await this.pgAdapter.getPaymentPlan(id)
    paymentPlan.start_date = toCalendarDate(paymentPlan.start_date)
    return addAmountPaidToInstalments(paymentPlan)
  }

  async get60And90DayOption (locationId) {
    const location = await this.pgAdapter.getLocation(locationId)
    return {
      locationId,
      classification: location.classification,
      isAvailable: location.classification === LOCATION_CLASSIFICATIONS.CLINICS_AND_HOSPITALS,
      has60And90DayOption: location.has60And90DayOption
    }
  }

  async enable60And90DayOption (locationId) {
    const location = await this.pgAdapter.getLocation(locationId)
    if (location.classification !== LOCATION_CLASSIFICATIONS.CLINICS_AND_HOSPITALS) {
      throw httpError(`60 and 90 day payment options only accessible to clinics and hospitals`, 400)
    }
    await this.pgAdapter.setLocation60And90DayOption(locationId, true)
    return { locationId, has60And90DayOption: true }
  }

  async disable60And90DayOption (locationId) {
    await this.pgAdapter.getLocation(locationId)
    await this.pgAdapter.setLocation60And90DayOption(locationId, false)
    return { locationId, has60And90DayOption: false }
  }

  async getPdfDownloadData (id) {
    const paymentPlan = await this.getOne(id)
    if (!paymentPlan.is_active || !paymentPlan.instalments.length) {
      throw httpError('The payment plan document can only be downloaded once the payment plan is active', 400)
    }
    const mobilizedDate = paymentPlan.location_mobilized_date
    return {
      locationName: paymentPlan.location_name,
      companyCode: paymentPlan.company_code,
      currency: paymentPlan.currency,
      pastDue: paymentPlan.past_due,
      startDate: paymentPlan.start_date,
      frequency: paymentPlan.frequency,
      term: paymentPlan.term,
      durationInWeeks: getPlanDurationInWeeks(paymentPlan.term, paymentPlan.frequency),
      serviceFee: paymentPlan.service_fee,
      weeksServed: mobilizedDate ? Math.floor((Date.now() - new Date(mobilizedDate)) / ONE_WEEK_MS) : null,
      instalments: paymentPlan.instalments.map((instalment, index) => ({
        number: index + 1,
        code: instalment.code,
        dueDate: instalment.due_date,
        amount: instalment.amount,
        balance: instalment.balance
      }))
    }
  }
}

module.exports = { PaymentPlanManagement }
